<script lang="ts" setup>
definePageMeta({
  middleware: ["guest"],
  layout: "empty",
});

const { t } = useI18n();
const localePath = useLocalePath();

useSeoMeta({
  title: t('seo.signup.title'),
  description: t('seo.signup.description'),
});

const email = ref<string>("");
const message = ref<string | null>(null);

const { data, execute, status } = useFetch("/api/auth/magic-link", {
  method: "POST",
  immediate: false,
  body: { email },
  watch: false,
});

watch(data, (newData) => {
  if (newData?.error) {
    safeCapture("magic_link_failed", { reason: newData?.error });
    message.value = newData?.error || null;
  } else {
    safeCapture("magic_link_sent", {
      redirect: newData?.redirectUrl || "/app",
      hasNext: Boolean(nextQueryParam),
    });
    navigateTo(localePath(newData?.redirectUrl || "/app"));
  }
});

// Handle the redirection after login
const nextCookie = useCookie("redirectAfterAuth");
const nextQueryParam = (useRoute().query?.next || null) as string | null;
nextCookie.value = nextQueryParam || null;

watchEffect(() => {
  const msg = useRoute().query?.message as string | null;
  if (msg) {
    message.value = msg;
  }
});

function resetMessage() {
  const route = useRoute();
  const router = useRouter();
  router.replace({
    ...route,
    query: {
      ...route.query,
      message: undefined,
    },
  });
}

async function handleSubmit() {
  const emailDomain = email.value?.split?.("@")?.[1] || null;
  safeCapture("magic_link_requested", {
    emailDomain,
    hasNext: Boolean(nextQueryParam),
  });

  await execute();
}
</script>

<template>
  <div class="flex h-1 min-h-svh flex-1 flex-col bg-neutral-100 px-6 py-12 sm:justify-center lg:px-8">
    <Logo
      to="/"
      class="my-10 justify-center"
    />

    <div class="mb-40 shrink-0 overflow-hidden rounded-xl bg-white px-4 py-5 shadow sm:mx-auto sm:px-10 sm:py-6">
      <div class="sm:mx-auto sm:w-full sm:max-w-sm">
        <h2 class="mt-3 text-center text-xl font-bold tracking-tight text-gray-900 sm:m-0">
          {{ $t('signup.title') }}
        </h2>
      </div>

      <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <UAlert
          v-if="message"
          icon="lucide:circle-x"
          color="red"
          class="mb-8"
          variant="soft"
          :title="message"
          :close-button="{ icon: 'heroicons:x-mark-20-solid', color: 'red', variant: 'ghost', padded: false }"
          @close="resetMessage"
        />

        <NuxtLink
          external
          to="/auth/google"
          class="mx-auto flex w-fit items-center justify-center gap-2 rounded-full bg-white px-3.5 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          <Icon
            name="logos:google-icon"
            class="size-5 shrink-0"
          />
          {{ $t('signup.continueWithGoogle') }}
        </NuxtLink>

        <!-- Or using magic-link email -->
        <div class="relative my-6">
          <div class="absolute inset-x-0 top-1/2 -translate-y-1/2 border-t border-gray-300" />
          <div class="relative flex justify-center text-sm font-medium text-gray-500">
            <span class="bg-white px-2 text-xs text-gray-500">{{ $t('common.or') }}</span>
          </div>
        </div>

        <form
          class="mx-auto max-w-[203px] space-y-4"
          @submit.prevent="handleSubmit"
        >
          <UInput
            v-model="email"
            :ui="{ rounded: 'rounded-full' }"
            :placeholder="$t('signup.emailPlaceholder')"
            name="email"
            type="email"
            :disabled="status === 'pending'"
            icon="lucide:mail"
          />
          <UButton
            v-show="email?.length"
            type="submit"
            :ui="{ rounded: 'rounded-full' }"
            class="w-full justify-center"
            :loading="status === 'pending'"
            :label="$t('signup.loginButton')"
          />
        </form>
      </div>
    </div>

    <p class="absolute inset-x-5 bottom-5 text-center text-xs text-gray-600">
      {{ $t('signup.termsNotice') }} <NuxtLinkLocale
        to="/legal/conditions-d-utilisation"
        target="_blank"
        class="text-primary-500"
      >
        {{ $t('signup.termsLink') }}
      </NuxtLinkLocale> {{ $t('signup.andOur') }} <NuxtLinkLocale
        to="/legal/confidentialite"
        target="_blank"
        class="text-primary-500"
      >
        {{ $t('signup.privacyLink') }}
      </NuxtLinkLocale>.
    </p>
  </div>
</template>
